import { useRef, useState } from 'react';
import { MAX_NAME_LENGTH, deriveInitials, normalizeName } from 'shared';
import { Avatar } from '../design/Avatar';
import { Button } from '../design/Button';
import { Panel } from '../design/Panel';
import { Label } from '../design/Paper';
import { ro } from '../i18n/ro';
import { downscaleAvatar } from '../state/avatar';
import { unlockAudio } from '../state/cues';
import { useRoom } from '../state/RoomConnection';

/**
 * Joining: a name and an optional photo. Guests go through this once and should
 * never have to think about it, so it is the one flow given real UX attention.
 *
 * The photo is chosen before joining but uploaded after, because the upload
 * needs the player token the join issues.
 */
export function JoinScreen({ onPendingAvatar }: { onPendingAvatar: (blob: Blob) => void }) {
  const room = useRoom();
  const [name, setName] = useState('');
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const trimmed = normalizeName(name);
  const canSubmit = trimmed.length > 0 && !submitting;

  async function pick(file: File | undefined) {
    if (!file) return;
    setPhotoError(null);
    try {
      const blob = await downscaleAvatar(file);
      setPhoto(blob);
      setPhotoUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return URL.createObjectURL(blob);
      });
    } catch {
      setPhotoError(ro.errors.AVATAR_INVALID);
    }
  }

  function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    // The join tap is the user gesture that unlocks audio; without it the vote
    // cue fails silently later.
    unlockAudio();
    if (photo) onPendingAvatar(photo);
    room.send({ type: 'join', name: trimmed });
    // Re-enabled if the server refuses (a taken name, say).
    window.setTimeout(() => setSubmitting(false), 1_500);
  }

  const preview = {
    id: 'preview',
    name: trimmed || ro.namePlaceholder,
    initials: trimmed ? deriveInitials(trimmed) : '?',
    colorIndex: room.snapshot?.players.length ?? 0,
    hasAvatar: false,
    avatarVersion: 0,
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col px-4 pb-5 pt-6">
      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div>
          <h1 className="font-display text-2xl uppercase leading-tight">{ro.chooseName}</h1>
          <Panel press="sm" className="mt-3" innerClassName="px-3 py-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              maxLength={MAX_NAME_LENGTH * 2}
              placeholder={ro.namePlaceholder}
              autoComplete="off"
              autoCapitalize="words"
              className="w-full bg-transparent text-base outline-none placeholder:text-ink-faint"
              aria-label={ro.chooseName}
              data-testid="join-name"
            />
          </Panel>
        </div>

        <div>
          <Label className="block text-[0.6rem] text-ink-soft">{ro.photoSection}</Label>
          <div className="mt-3 flex items-center gap-3">
            <Panel press="sm" rule innerClassName="p-1.5">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="h-24 w-24 object-cover" />
              ) : (
                <Avatar subject={preview} roomToken={null} size="lg" />
              )}
            </Panel>
            <div className="flex flex-1 flex-col gap-2">
              <Button tone="paper" size="sm" block onClick={() => cameraRef.current?.click()}>
                {ro.takePhoto}
              </Button>
              <Button tone="ghost" size="sm" block onClick={() => galleryRef.current?.click()}>
                {ro.addPhoto}
              </Button>
              {photo && (
                <Button
                  tone="ghost"
                  size="sm"
                  block
                  onClick={() => {
                    setPhoto(null);
                    setPhotoUrl((p) => {
                      if (p) URL.revokeObjectURL(p);
                      return null;
                    });
                  }}
                >
                  {ro.removePhoto}
                </Button>
              )}
            </div>
          </div>
          {photoError && <p className="mt-2 text-xs text-red">{photoError}</p>}

          {/* `capture` opens the camera directly on a phone. */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="user"
            hidden
            onChange={(e) => void pick(e.target.files?.[0])}
          />
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => void pick(e.target.files?.[0])}
          />
        </div>
      </div>

      <div className="mt-6 shrink-0">
        <Button
          tone="ink"
          size="lg"
          block
          disabled={!canSubmit}
          data-testid="join-submit"
          onClick={submit}
        >
          {submitting ? ro.joining : ro.join}
        </Button>
      </div>
    </main>
  );
}
